import withStyles from '@material-ui/core/styles/withStyles';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import ActivitiesContainer from '../../../../components/Activities/ActivitiesContainer/ActivitiesContainer';
import { summaryActivitiesMapping } from '../../../../components/Activities/componentsMappings';
import EmptyActivitiesIndicator from '../../../../components/Activities/EmptyActivitiesIndicator/EmptyActivitiesIndicator';
import GroupedByTimeActivities from '../../../../components/Activities/GroupedByTimeActivities/GroupedByTimeActivities';
import ErrorSnackBars from '../../../../components/Errors/ErrorSnackBars/ErrorSnackBars';
import DuplicateAct from '../../../ActForm/DuplicateAct/DuplicateAct';
import DuplicateTravelExpenses from '../../../TravelExpenses/DuplicateTravelExpenses';
import { selectActivitiesInContext, selectActivitiesLoading } from '../../../Activities/selectors';
import {
  ACTIVITY_ID_ROUTE_PARAM,
  ACTIVITY_TYPE_ROUTE_PARAM,
  DUPLICATE_TRAVEL_EXPENSES_ROUTE,
  DUPLICATE_ACT_ROUTE
} from '../../../Navigation/constants';
import AdminActivityDetail from './AdminActivityDetail/AdminActivityDetail';
import AdminSearchBox from './ActivitiesSearchBox/ActivitiesSearchBox';
import { gql, useQuery } from '@apollo/client';
import { useLocation } from 'react-use';

export const styles = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    flex: 1
  },
  userActivities: {
    maxWidth: 980,
    width: 'unset',
    height: 'calc(100vh - 160px)'
  },
  duplicateActWrapper: {
    overflow: 'auto',
    flex: 1
  },
  card: {
    marginTop: 0,
    maxWidth: 'unset'
  }
});

const MAX_ACTIVITIES_PER_PAGE = 1000;

const SEARCH_USER_ACTIVITIES_QUERY = gql`
  query SearchUserActivities(
    $query: ActivitiesQuery!
    $limit: Int!
    $offset: Int!
    $sortKey: ActivitiesSortKey
    $sortDirection: SortDirection
  ) {
    activitiesResult(query: $query, limit: $limit, offset: $offset, sortKey: $sortKey, sortDirection: $sortDirection) {
      count
      activities {
        id
        firebaseId
        date
      }
    }
  }
`;

export function UserActivities({ activities, activitiesLoading, classes }) {
  const location = useLocation();
  const userId = location.hash.match(/\/users\/([^\/]+)\/activities/)?.[1];
  const [filters, setFilters] = useState({});
  const [filteredActivities, setFilteredActivities] = useState();

  const variables = {
    limit: MAX_ACTIVITIES_PER_PAGE,
    offset: 0,
    sortKey: 'date',
    sortDirection: 'asc',
    query: {
      userIds: userId ? [userId] : [],
      ...(filters.textFilter && { searchTerm: filters.textFilter }),
      ...(filters.type && { types: filters.type }),
      ...(filters.status && { statuses: filters.status }),
      ...(filters.adminStatus && { adminStatuses: filters.adminStatus }),
      ...(filters.outsideRamqPatientType && {
        outsideRamqPatientTypes: filters.outsideRamqPatientType
      }),
      ...(filters.other && filters.other.includes(ActivityOtherFilter.UNSYNCHRONIZED) && { synchronized: false }),
      ...(filters.dateRange?.startDate && {
        startDate: Number(filters.dateRange.startDate)
      }),
      ...(filters.dateRange?.endDate && {
        endDate: Number(filters.dateRange.endDate)
      })
    }
  };

  const { data, loading } = useQuery(SEARCH_USER_ACTIVITIES_QUERY, {
    variables,
    skip: !filters.textFilter
  });

  const firebaseIds = useMemo(
    () => data?.activitiesResult.activities.map(({ firebaseId }) => firebaseId) ?? [],
    [data?.activitiesResult.activities]
  );

  // Filter out activities that don't match Ralph's search terms
  useEffect(() => {
    if (loading || !filters.textFilter) {
      setFilteredActivities(undefined);
      return;
    }

    const filteredActivities = [...activities].filter((activity) => firebaseIds.includes(activity.id));

    if (filteredActivities.length !== activities.length) {
      setFilteredActivities(filteredActivities);
    }
  }, [activities, filters.textFilter, firebaseIds, loading]);

  return (
    <>
      <AdminSearchBox onFilterChange={setFilters} />
      <div className={classes.wrapper}>
        <ActivitiesContainer className={classes.userActivities}>
          <GroupedByTimeActivities
            activitiesLoading={activitiesLoading}
            highlightSelected
            activities={filteredActivities ?? activities}
            componentsMapping={summaryActivitiesMapping}
            EmptyIndicatorComponent={EmptyActivitiesIndicator}
          />
        </ActivitiesContainer>
        <Routes>
          <Route
            path={DUPLICATE_ACT_ROUTE}
            element={
              <div className={classes.duplicateActWrapper}>
                <DuplicateAct />
              </div>
            }
          />
          <Route
            path={DUPLICATE_TRAVEL_EXPENSES_ROUTE}
            element={
              <div className={classes.duplicateActWrapper}>
                <DuplicateTravelExpenses />
              </div>
            }
          />
          <Route path={ACTIVITY_TYPE_ROUTE_PARAM}>
            <Route
              path={ACTIVITY_ID_ROUTE_PARAM}
              element={activitiesLoading ? null : <AdminActivityDetail activities={activities} />}
            />
          </Route>
        </Routes>
      </div>
      <ErrorSnackBars />
    </>
  );
}

UserActivities.propTypes = {
  activities: PropTypes.array.isRequired,
  activitiesLoading: PropTypes.bool.isRequired
};

export const mapStateToProps = createStructuredSelector({
  activities: selectActivitiesInContext(),
  activitiesLoading: selectActivitiesLoading()
});

export default compose(withStyles(styles), connect(mapStateToProps))(UserActivities);
